import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button
} from "reactstrap";

import Api from "../../components/helpers/Api";
import { SeoHelper } from "../../components/helpers/SeoHelper";
import { RiMusic2Line } from "react-icons/ri";
import KstTrackLinks from "../../components/kst/KstTrackLinks";

export function formatThemeSlug(value) {
    if (!value) return "";

    return value
        .toString()
        .toLowerCase()
        .trim()
        // remove apostrophes (God's → gods)
        .replace(/['']/g, "")
        // replace non-alphanumeric with hyphens
        .replace(/[^a-z0-9]+/g, "-")
        // remove leading/trailing hyphens
        .replace(/^-+|-+$/g, "")
        // collapse multiple hyphens
        .replace(/-{2,}/g, "-");
}

export default function ThemeSongsPage() {

    const { themeId, slug } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        Api.get(`/api/themes/${themeId}/seo`)
            .then(res => setData(res.data))
            .catch(err => console.error("Failed to load theme songs", err));
    }, [themeId]);

    // Redirect GUID-only URLs to canonical slug form once title is known
    useEffect(() => {
        if (data && !slug) {
            navigate(`/themes/${themeId}/${formatThemeSlug(data.title)}`, { replace: true });
        }
    }, [data, slug, themeId, navigate]);

    useEffect(() => {
        if (!data) return;

        if (typeof window.gtag === 'function') {
            window.gtag('event', 'view_song_list', {
                content_type: 'theme',
                theme: data.title,
                source: 'browse'
            });
        }
    }, [data]);

    const canonicalSlug = data ? formatThemeSlug(data.title) : slug;
    const canonical = canonicalSlug
        ? `/themes/${themeId}/${canonicalSlug}`
        : `/themes/${themeId}`;

    const themeName = data?.title ?? (slug
        ? slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : "this Theme");

    return (
        <>
            <SeoHelper
                uri={canonical}
                title={`Worship Songs About ${themeName}`}
                description={data?.title
                    ? `Discover worship songs related to ${data.title}. Find songs connected to this Biblical theme for worship planning.`
                    : `Discover worship songs related to ${themeName}.`}
            />

            {!data ? (
                <div className="text-center mt-5">Loading...</div>
            ) : (() => {

                const visibleWords = data.words;

                const startPlanning = () => {
                    if (typeof window.gtag === 'function') {
                        window.gtag('event', 'select_song', {
                            source: 'themes',
                            theme: data.title
                        });
                    }

                    sessionStorage.setItem(
                        "selected-themes",
                        JSON.stringify([data])
                    );
                    window.location.href = "/themes/choose-themes";
                };

                return (
                    <section className="section section-lg">

                        <Container>

                            <Row className="justify-content-center">

                                <Col lg="9" xl="8">

                                    <h1 className="display-4 text-center text-primary mb-3">
                                        Worship Songs About {data.title}
                                    </h1>

                                    <p className="lead text-center text-muted mb-4">
                                        Discover worship songs connected with the theme of {data.title}.
                                    </p>

                                    <Card className="mb-4">
                                        <CardBody>

                                            <h2 className="h4 mb-3">
                                                <RiMusic2Line size={22} style={{ marginRight: "8px" }} />
                                                Songs Related to {data.title}
                                                <span className="text-muted ml-2" style={{ fontSize: "0.85em", fontWeight: 400 }}>
                                                    ({data.words.length})
                                                </span>
                                            </h2>

                                            {data.words?.length > 0 ? (
                                                <ul className="twb-grid">
                                                    {visibleWords.map(word => (
                                                        <li key={word.id}>
                                                            <div className="twb-next-step">
                                                                <div className="twb-next-step-icon">
                                                                    <RiMusic2Line size={18}/>
                                                                </div>

                                                                <div className="twb-next-step-text">
                                                                    <div style={{fontWeight: "500"}}>
                                                                        {word.title}
                                                                    </div>
                                                                    <KstTrackLinks kstTracks={word.kstTracks} seo />

                                                                    {word.alternativeTitle && (
                                                                        <div className="small text-muted">
                                                                            {word.alternativeTitle}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-muted">
                                                    There are currently no songs for this theme.
                                                </p>
                                            )}

                                            {!sessionStorage.getItem("customer") && (
                                                <div className="alert alert-light border d-flex justify-content-between align-items-center mt-4">
                                                    <span>
                                                        Planning a service? Create an account to save your song-flow and come back to it later.
                                                    </span>
                                                    <a href="/register/choose-your-plan" className="btn btn-sm btn-primary ml-3">
                                                        Create account →
                                                    </a>
                                                </div>
                                            )}

                                        </CardBody>
                                    </Card>

                                    <Card className="mb-4">
                                        <CardBody className="text-center">

                                            <h3 className="h5 mb-3">
                                                Plan a Worship Service Around {data.title}
                                            </h3>

                                            <Button color="primary" onClick={startPlanning}>
                                                Start Planning a Song-Flow
                                            </Button>

                                        </CardBody>
                                    </Card>

                                </Col>

                            </Row>

                        </Container>

                    </section>
                );

            })()}
        </>
    );
}
